import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Alert, Col, Grid, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Back, MedicalRecord } from '../../Icons/Icons';
import { getDeletedVisitAPI } from '../../../api/protocol';
import { DynamicForm } from '../../DynamicForm/DynamicForm';
import { HandleApiError } from '../../Notifications/APIErrorHandler';
import { Spinner } from '../../Utils/Loaders';
import { EvolutionsContainer } from '../../../containers/Protocols/Evolutions/Evolutions';
import { EntryItemsListTabs } from '../../../containers/MedicalRecords/MedicalRecordItems/EntryItemsList';
import { Slider } from '../../Slider/Slider';
import { FormMenu, VisitStatus } from './Forms';
import { PatientStatusInProtocol } from '../Patients';
import { WithAuth } from '../../Authorization/WithAuthProtocol';
import DocumentNotesContainer from '../../../containers/Protocols/DocumentNotes/DocumentNotes';
import MonitorNotesContainer from '../../../containers/Protocols/MonitorNotes/MonitorNotes';
import { dateTimeUTCFormatter } from '../../Utils/Formatter';

const REASON_LABELS = {
    0: 'visitTracking_removeVisit_reason_loadError',
    1: 'visitTracking_removeVisit_reason_other'
};

export const DeletedVisitViewer = ({ logId }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedForm, setSelectedForm] = useState(null);
    const [showHistory, setShowHistory] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await getDeletedVisitAPI(logId);
                setData(res.data);
                if (res.data?.forms?.length > 0)
                    setSelectedForm(res.data.forms[0]);
            } catch (error) {
                HandleApiError(error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [logId]);

    if (isLoading) return <Spinner />;
    if (!data) return null;

    const { deletionInfo, visitTracking, forms, patientFullName, patientNumber, patientInProtocolStatus } = data;

    const reasonLabel = t(`protocolNotifications.${REASON_LABELS[deletionInfo.reasonType]}`);
    const reasonDisplay = deletionInfo.reasonType === 1 && deletionInfo.reasonText
        ? `${reasonLabel} — ${deletionInfo.reasonText}`
        : reasonLabel;

    const preparedForm = selectedForm
        ? {
            ...selectedForm,
            fields: typeof selectedForm.fields === 'string'
                ? JSON.parse(selectedForm.fields)
                : (selectedForm.fields ?? [])
          }
        : {};

    return (
        <>
            <Alert bsStyle="danger">
                <strong>{t('protocolNotifications.visitTracking_deletedVisit_banner_title')}</strong>
                {' · '}
                {t('protocolNotifications.visitTracking_deletedVisit_banner_by')}: <strong>{deletionInfo.deletedByUserName}</strong>
                {' · '}
                {dateTimeUTCFormatter(deletionInfo.deletedDate)}
                <div style={{ marginTop: '4px' }}>
                    {t('protocolNotifications.visitTracking_deletedVisit_banner_reason')}: <strong>{reasonDisplay}</strong>
                </div>
            </Alert>

            <Formik initialValues={preparedForm} enableReinitialize onSubmit={() => {}}>
                {props => (
                    <Row>
                        <Col md={4} lg={3}>
                            <FormMenu
                                forms={forms}
                                onClick={(formId) => {
                                    const form = forms.find(f => f.id === formId);
                                    if (form) setSelectedForm(form);
                                }}
                                selectedFormId={selectedForm?.id}
                                formsWithErrors={[]}
                            />
                            <button
                                type="button"
                                className="btn btn-primary btn-fill btn-full"
                                onClick={() => history.goBack()}
                            >
                                <Back /> {t('buttons.back')}
                            </button>
                        </Col>
                        <Col md={4} lg={5}>
                            <VisitStatus status={visitTracking?.status} date={visitTracking?.created} />
                            <DynamicForm
                                {...props}
                                patientId={visitTracking?.patientId}
                                visitId={visitTracking?.visitId}
                                protocolId={visitTracking?.protocolId}
                                isDisabled={true}
                                isUploadEnabled={false}
                            />
                            <WithAuth protocolId={visitTracking?.protocolId} requiredPermission={["InternalNotes.List"]}>
                                <DocumentNotesContainer
                                    protocolId={visitTracking?.protocolId}
                                    documentId={selectedForm?.id}
                                    visitId={visitTracking?.id}
                                    patientId={patientNumber}
                                    type="VisitTracking"
                                    isReadOnly={true}
                                />
                            </WithAuth>
                        </Col>
                        <Col md={4} lg={4} className="sticky">
                            <button
                                type="button"
                                className="btn btn-primary btn-fill btn-full"
                                onClick={() => setShowHistory(!showHistory)}
                                style={{ marginBottom: '22px', textAlign: 'center' }}
                            >
                                <MedicalRecord /> {t('protocols.visitTracking.viewMR')}
                            </button>
                            <EvolutionsContainer
                                {...props}
                                visitType="VisitTracking"
                                protocolId={visitTracking?.protocolId}
                                visitId={visitTracking?.id}
                                patientId={visitTracking?.patientId}
                                medicalRecordNumber={visitTracking?.patientId}
                                visitTrackingStatus={visitTracking?.status}
                                data={visitTracking?.evolutions ?? []}
                                isDisabled={true}
                                title={t('protocols.visitTracking.patientEvolution')}
                                subtitle={<><PatientStatusInProtocol status={patientInProtocolStatus} /> {patientNumber} {patientFullName}</>}
                            />
                            <WithAuth protocolId={visitTracking?.protocolId} requiredPermission={["ExternalNotes.List"]}>
                                <MonitorNotesContainer
                                    protocolId={visitTracking?.protocolId}
                                    documentId={selectedForm?.id}
                                    visitId={visitTracking?.id}
                                    patientId={patientNumber}
                                    type="VisitTracking"
                                    isReadOnly={true}
                                />
                            </WithAuth>
                        </Col>
                        <Slider isOpen={showHistory}>
                            <Grid fluid>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-fill btn-full"
                                    onClick={() => setShowHistory(!showHistory)}
                                    style={{ margin: '22px 0', textAlign: 'center' }}
                                >
                                    <MedicalRecord /> {t('protocols.visitTracking.hideMR')}
                                </button>
                                <EntryItemsListTabs medicalRecordNumber={visitTracking?.patientId} inProtocol />
                            </Grid>
                        </Slider>
                    </Row>
                )}
            </Formik>
        </>
    );
};
